import { useEffect, useRef } from 'react';
import {
  DEFAULT_BARBELL_KG,
  DEFAULT_LOADABLE_STEP_KG,
  DEFAULT_PLATE_INVENTORY,
  smallestLoadableStepKg,
  type PlateInventoryEntry,
} from '@jackedlogs/shared';

// web-rnw twin of ./plateConfig.ts (see that file's header) — same
// keys/shape, localStorage instead of AsyncStorage.

const BAR_KEY = 'jackedlogs.plateBar.v1';
const INV_KEY = 'jackedlogs.plateInventory.v1';

export const BAR_OPTIONS_KG: ReadonlyArray<number> = [20, 15, 10, 7.5, 7, 5, 0];

export async function loadPlateBarKg(): Promise<number> {
  try {
    const raw = localStorage.getItem(BAR_KEY);
    if (!raw) return DEFAULT_BARBELL_KG;
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n < 0) return DEFAULT_BARBELL_KG;
    return n;
  } catch {
    return DEFAULT_BARBELL_KG;
  }
}

export async function persistPlateBarKg(kg: number): Promise<void> {
  try {
    localStorage.setItem(BAR_KEY, String(kg));
  } catch {
    // ignored
  }
}

function parseStoredInventory(raw: string | null): PlateInventoryEntry[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    const cleaned: PlateInventoryEntry[] = [];
    for (const item of parsed) {
      if (
        item &&
        typeof item === 'object' &&
        typeof (item as { kg?: unknown }).kg === 'number' &&
        typeof (item as { pairs?: unknown }).pairs === 'number'
      ) {
        const kg = (item as { kg: number }).kg;
        const pairs = (item as { pairs: number }).pairs;
        if (kg > 0 && pairs >= 0) cleaned.push({ kg, pairs });
      }
    }
    return cleaned.length > 0 ? cleaned : null;
  } catch {
    return null;
  }
}

export async function loadPlateInventory(): Promise<PlateInventoryEntry[]> {
  try {
    const raw = localStorage.getItem(INV_KEY);
    return (
      parseStoredInventory(raw) ?? DEFAULT_PLATE_INVENTORY.map((e) => ({ ...e }))
    );
  } catch {
    return DEFAULT_PLATE_INVENTORY.map((e) => ({ ...e }));
  }
}

export async function loadRoundingStepKg(): Promise<number> {
  try {
    const custom = parseStoredInventory(localStorage.getItem(INV_KEY));
    if (!custom) return DEFAULT_LOADABLE_STEP_KG;
    return smallestLoadableStepKg(custom, 'pairs');
  } catch {
    return DEFAULT_LOADABLE_STEP_KG;
  }
}

export async function persistPlateInventory(
  inv: ReadonlyArray<PlateInventoryEntry>,
): Promise<void> {
  try {
    localStorage.setItem(INV_KEY, JSON.stringify(inv));
  } catch {
    // ignored
  }
}

// Web twin of the native hook. ⚠️ It has to exist HERE too: the two files are
// a platform-resolved pair, so a hook added only to the native side compiles
// clean and then explodes at runtime on web-rnw — the failure mode the whole
// `.web.ts` split exists to make visible. The body is identical because only
// `loadRoundingStepKg` differs (localStorage vs AsyncStorage), and that
// difference is already resolved by which file the bundler picked.
export function useRoundingStepKgRef(): { current: number | undefined } {
  const ref = useRef<number | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const step = await loadRoundingStepKg();
      if (!cancelled) ref.current = step;
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return ref;
}
